import { Action, Dispatch } from 'redux';
import { get, set } from 'utils/idb-keyval';
import { getMainMenu } from 'services/ApiAppShellService';
import { IMenuItem } from 'models/IMenuItem';
import { IMainMenu } from 'models/IMainMenu';
import { LoadingStatus } from 'models/LoadingStatus';
import { BasePageModel } from 'models/BasePageModel';
import { handleServerError } from 'components/Shared/server';
import batchAction from 'store/batching';
import { addHours, isFuture } from 'date-fns';
export const MAIN_MENU_LEVEL_TOGGLE = 'MAIN_MENU_LEVEL_TOGGLE';
export const MAIN_MENU_LEVEL_OPEN = 'MAIN_MENU_LEVEL_OPEN';
export const MAIN_MENU_LEVEL_CLOSE = 'MAIN_MENU_LEVEL_CLOSE';
export const MAIN_MENU_OPEN = 'MAIN_MENU_OPEN';
export const MAIN_MENU_CLOSE = 'MAIN_MENU_CLOSE';
export const MAIN_MENU_TOGGLE = 'MAIN_MENU_TOGGLE';

export interface ToggleMenuAction extends Action, ToggleMenuParams {}
export interface ToggleMenuParams {
  parents: string[];
  target: string;
}

export interface OpenCloseMenuAction extends Action, OpenCloseMenuParams {}
export interface OpenCloseMenuParams {
  parents: string[];
  target: string;
}

export const toggleMainMenu = () => ({
  type: MAIN_MENU_TOGGLE
});

export const openMainMenu = () => ({
  type: MAIN_MENU_OPEN
});

export const closeMainMenu = () => ({
  type: MAIN_MENU_CLOSE
});

export interface MainMenuAction extends Action {
  menu: IMenuItem[];
}

export const toggleMenu = (action: ToggleMenuParams): ToggleMenuAction => ({
  type: MAIN_MENU_LEVEL_TOGGLE,
  ...action
});

export const openMenu = (action: OpenCloseMenuParams): OpenCloseMenuAction => ({
  type: MAIN_MENU_LEVEL_OPEN,
  ...action
});

export const closeMenu = (action: OpenCloseMenuParams): OpenCloseMenuAction => ({
  type: MAIN_MENU_LEVEL_CLOSE,
  ...action
});

export const openMenuItem =
  ({ parents, target }: OpenCloseMenuParams) =>
  (dispatch: Dispatch) => {
    dispatch(batchAction(openMenu({ parents, target }), openMainMenu()));
  };

export const closeMenuItem =
  ({ parents, target }: OpenCloseMenuParams) =>
  (dispatch: Dispatch) => {
    dispatch(closeMenu({ parents, target }));
    dispatch(closeMainMenu());
  };

export const MAIN_MENU = 'MAIN_MENU';
export const setMainMenu = (menu: IMenuItem[]): MainMenuAction => ({
  type: MAIN_MENU,
  menu
});

const STORAGE_KEY_MAIN_MENU = 'MAIN_MENU';
const STORAGE_KEY_MAIN_MENU_CACHE = 'MAIN_MENU_CACHE';
const STORAGE_KEY_MAIN_MENU_HASH = 'MAIN_MENU_HASH';

/*
 * For private windows in FF and Edge there is no access to indexedDB and idb-keyval throws an error
 * We try and catch that here to simply fetch the menu every time instead.
 */
const handleDbAccessError =
  (hash: string) =>
  (e: Error): Promise<[IMenuItem[], Date, string]> => {
    if (!(e instanceof ReferenceError || e instanceof DOMException)) {
      throw e;
    }
    return getMainMenu().then(menu => [menu, getCacheTime(), hash]);
  };
const getCacheTime = () => addHours(Date.now(), 1);
export const loadMenu = (newHash: string) => (dispatch: Dispatch) => {
  Promise.all([
    get<IMenuItem[]>(STORAGE_KEY_MAIN_MENU),
    get<number>(STORAGE_KEY_MAIN_MENU_CACHE),
    get<string>(STORAGE_KEY_MAIN_MENU_HASH)
  ])
    .catch(handleDbAccessError(newHash))
    .then(([menu, cache, hash]) =>
      menu !== undefined && isFuture(cache) && newHash === hash
        ? Promise.resolve(menu)
        : getMainMenu().then(menu => {
            void set(STORAGE_KEY_MAIN_MENU, menu);
            void set(STORAGE_KEY_MAIN_MENU_CACHE, getCacheTime());
            void set(STORAGE_KEY_MAIN_MENU_HASH, newHash);
            return menu;
          })
    )
    .then(menu => dispatch(setMainMenu(menu)))
    .catch(handleServerError());
};

const WEB_CATALOG_NOT_PINNED: string[] = ['SVES', 'NORS', 'SVEV', 'NORV'];
const WEB_CATALOG_START = 0;
const WEB_CATALOG_END = 4;

type IsPinned = Pick<IMainMenu, 'webCatalog'>;
export const isPinned = ({ webCatalog }: IsPinned) =>
  webCatalog !== '' && !WEB_CATALOG_NOT_PINNED.find(w => webCatalog.includes(w));

type IsCatalogPinned = Pick<IMainMenu, 'isOpen' | 'webCatalog'>;
type MenuId = Pick<IMenuItem, 'id'>;
export const isCatalogPinned = (mainMenu: IsCatalogPinned, { id }: MenuId) =>
  isPinned(mainMenu) && mainMenu.webCatalog.substr(WEB_CATALOG_START, WEB_CATALOG_END) === id;

const isPageDoing = (status: LoadingStatus) => (currentPage: BasePageModel) =>
  currentPage.status === status;
export const isPageLoading = isPageDoing(LoadingStatus.Loading);
export const isPageFetching = isPageDoing(LoadingStatus.Fetching);
export const isPageCompleted = isPageDoing(LoadingStatus.Completed);

export const isMenuOpen = ({ open }: Pick<IMainMenu, 'open'>, { id }: MenuId) => open.includes(id);
